/**
 * Client-side application-type advisor (mirrors server catalog matching).
 * Prefer POST /api/v1/application-type/advise when the API is available.
 * Pass a jurisdiction catalog for multi-city; defaults to SF bundled catalog.
 */
import sfCatalog from '../data/jurisdictions/san-francisco/v1/catalog.json';
import berkeleyCatalog from '../data/jurisdictions/berkeley/v1/catalog.json';
import demoCatalog from '../data/jurisdictions/demo-generic/v1/catalog.json';
import { refineApplicationRecommendations } from '../../../packages/builder-schema/src/refineApplicationRecommendations.mjs';

const CATALOG_BY_ID = {
  'san-francisco': sfCatalog,
  berkeley: berkeleyCatalog,
  'demo-generic': demoCatalog,
};

const TRACK_LABELS = {
  permit: 'Building permit',
  planning: 'Planning / zoning',
  license: 'Business license / registration',
};

function resolveCatalog(catalogOrId) {
  if (catalogOrId && typeof catalogOrId === 'object' && Array.isArray(catalogOrId.applicationTypes)) {
    return catalogOrId;
  }
  const id = String(catalogOrId || 'san-francisco').trim() || 'san-francisco';
  return CATALOG_BY_ID[id] || sfCatalog;
}

function normalizeCategory(category, catalog) {
  if (!category) return '';
  const aliases = catalog.categoryAliases || {};
  const raw = String(category).trim();
  return aliases[raw] || aliases[raw.toLowerCase()] || raw.toLowerCase().replace(/\s+/g, '_');
}

function buildHaystack(intent = {}) {
  const parts = [
    intent.description,
    intent.projectType,
    intent.category,
    intent.location,
    intent.address,
    ...(Array.isArray(intent.tags) ? intent.tags : []),
  ];
  return parts.filter(Boolean).join(' ').toLowerCase();
}

function matchRule(rule, haystack, category) {
  const cats = Array.isArray(rule.categories) ? rule.categories : [];
  const keywords = Array.isArray(rule.anyKeywords) ? rule.anyKeywords : [];
  const keywordHit = keywords.some((kw) => haystack.includes(String(kw).toLowerCase()));
  const categoryHit = Boolean(category && cats.length && cats.includes(category));
  // Mirror server: keywords are authoritative; property-type categories must not block.
  if (keywords.length) {
    if (!keywordHit) return false;
    if (cats.length && category && cats.includes(category)) return true;
    if (cats.length && category && !cats.includes(category)) return true;
    return true;
  }
  if (cats.length) return categoryHit;
  return false;
}

function resolveApplicationTypes(ids = [], catalog) {
  const byId = new Map((catalog.applicationTypes || []).map((t) => [t.id, t]));
  const sourceById = new Map((catalog.sources || []).map((s) => [s.id, s]));
  return ids
    .map((id) => byId.get(id))
    .filter(Boolean)
    .map((t) => ({
      id: t.id,
      label: t.label,
      track: t.track,
      typical: t.typical || null,
      nextSteps: Array.isArray(t.nextSteps) ? t.nextSteps : [],
      sources: (t.sourceIds || [])
        .map((sid) => sourceById.get(sid))
        .filter(Boolean)
        .map((s) => ({ id: s.id, title: s.title, url: s.url })),
    }));
}

function buildChecklist(applicationTypes, primaryTrack) {
  const priorityFor = (t) => {
    if (t.role === 'start') return 'Start here';
    if (t.role === 'maybe') return 'If applicable';
    if (t.role === 'required') return 'Required';
    return t.track === primaryTrack ? 'Primary' : 'Also needed';
  };
  return {
    columns: [
      { field: 'application', headerName: 'Application', width: 200 },
      { field: 'track', headerName: 'Track', width: 120 },
      { field: 'typical', headerName: 'Typical timing', width: 160 },
      { field: 'priority', headerName: 'Priority', width: 100 },
    ],
    rows: applicationTypes.map((t, i) => ({
      id: t.id || `row-${i}`,
      application: t.label,
      track: TRACK_LABELS[t.track] || t.track,
      typical: t.typical || 'Varies',
      priority: priorityFor(t),
    })),
  };
}

function buildSummaryMarkdown(catalog, { primaryTrack, rationale, applicationTypes, sources }) {
  const trackMeta = (catalog.tracks || []).find((t) => t.id === primaryTrack);
  const trackLabel = trackMeta?.label || TRACK_LABELS[primaryTrack] || primaryTrack;
  const lines = [
    `## Recommended path: **${trackLabel}**`,
    '',
    rationale,
    '',
    '### Applications to consider',
    ...applicationTypes.map((t) => `- **${t.label}** (${TRACK_LABELS[t.track] || t.track})`),
    '',
    '### Next steps',
    ...applicationTypes.flatMap((t) => (t.nextSteps || []).map((s) => `- ${s}`)),
    '',
    `_${catalog.disclaimer || 'Guidance only — not an official determination.'}_`,
  ];
  if (sources.length) {
    lines.push('', '### Sources');
    sources.forEach((s) => lines.push(`- [${s.title}](${s.url})`));
  }
  return lines.join('\n');
}

export function getSfCatalog() {
  return sfCatalog;
}

export function getCatalogById(jurisdictionId = 'san-francisco') {
  return resolveCatalog(jurisdictionId);
}

/**
 * @param {object} intent
 * @param {object|string} [catalogOrId] — full catalog object or package id
 */
export function adviseApplicationType(intent = {}, catalogOrId = 'san-francisco') {
  const catalog = resolveCatalog(catalogOrId);
  const category = normalizeCategory(intent.category || intent.projectType, catalog);
  // Prefer normalized category over UI labels (e.g. "Plumbing / Electrical").
  const haystack = buildHaystack({
    ...intent,
    category,
    projectType: category || intent.projectType,
  });
  const rules = [...(catalog.rules || [])].sort((a, b) => (a.priority || 100) - (b.priority || 100));
  let matched = null;
  for (const rule of rules) {
    if (matchRule(rule, haystack, category)) {
      matched = rule;
      break;
    }
  }
  const fallback = catalog.fallback || {
    primaryTrack: 'permit',
    applicationTypeIds: (catalog.applicationTypes || []).slice(0, 1).map((t) => t.id),
    rationale: 'No specific rule matched — review the applications below with the local department.',
    confidence: 0.4,
  };
  const chosen = matched || fallback;
  let applicationTypes = resolveApplicationTypes(chosen.applicationTypeIds || [], catalog);
  const primaryTrack = chosen.primaryTrack || applicationTypes[0]?.track || 'permit';
  const baseConfidence = typeof chosen.confidence === 'number' ? chosen.confidence : 0.5;
  let rationale = chosen.rationale || fallback.rationale;

  const refined = refineApplicationRecommendations({
    applicationTypes,
    primaryTrack,
    matchedRuleId: matched?.id || null,
    matchedRule: matched,
    policy: catalog.recommendationPolicy || null,
    intent,
    scopingAnswers: {
      servesAlcohol: intent.servesAlcohol,
      outdoorSeating: intent.outdoorSeating,
      foodHandling: intent.foodHandling,
      structuralChange: intent.structuralChange,
      approxSqft: intent.approxSqft ?? intent.sqft,
    },
    baseConfidence,
    needsFollowUp: false,
  });
  applicationTypes = refined.applicationTypes;
  const maybeApplicationTypes = refined.maybeApplicationTypes || [];
  const confidence = refined.confidence;
  if (maybeApplicationTypes.length) {
    const labels = maybeApplicationTypes.map((t) => t.label).filter(Boolean).slice(0, 3);
    rationale = `${rationale} Other reviews may apply depending on details (${labels.join(', ')}${maybeApplicationTypes.length > 3 ? '…' : ''}).`.trim();
  }

  const sourceIds = new Set();
  applicationTypes.forEach((t) => (t.sources || []).forEach((s) => sourceIds.add(s.id)));
  const sources = (catalog.sources || [])
    .filter((s) => sourceIds.has(s.id))
    .map((s) => ({ id: s.id, title: s.title, url: s.url, track: s.track }));

  const nextSteps = [];
  applicationTypes.forEach((t) => {
    (t.nextSteps || []).forEach((step) => {
      if (!nextSteps.includes(step)) nextSteps.push(step);
    });
  });

  const routing = {
    jurisdictionId: catalog.id,
    jurisdictionName: catalog.name,
    primaryTrack,
    primaryTrackLabel: TRACK_LABELS[primaryTrack] || primaryTrack,
    confidence,
    rationale,
    matchedRuleId: matched?.id || null,
    applicationTypes,
    maybeApplicationTypes,
    recommendationMode: refined.recommendationMode,
    nextSteps,
    sources,
    disclaimer: catalog.disclaimer,
  };

  return {
    success: true,
    result: {
      routing,
      checklist: buildChecklist(applicationTypes, primaryTrack),
      summary: {
        content: buildSummaryMarkdown(catalog, {
          primaryTrack,
          rationale: routing.rationale,
          applicationTypes,
          sources,
        }),
      },
    },
  };
}

/**
 * Call server advise API; fall back to local catalog matching.
 */
export async function adviseApplicationTypeRemote(intent = {}, { jurisdictionId = 'san-francisco' } = {}) {
  try {
    const res = await fetch('/api/v1/application-type/advise', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ jurisdictionId, intent }),
    });
    if (res.ok) {
      const data = await res.json();
      if (data?.success && data?.result) return data;
    }
  } catch {
    // fall through to local
  }
  return adviseApplicationType(intent, jurisdictionId);
}
